import React, { useEffect, useState, useCallback, useContext, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { ArrowLeft, Home, Warehouse, Search, XCircle, Plus, Trash2, ChevronUp, ChevronDown, FolderInput, Pencil, Gauge } from 'lucide-react';
import API_URL from '@/config/api';
import AuthContext from '@/contexts/AuthContext';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '@/components/ui/dialog';
import { toast } from 'sonner';
import { useConfirm } from '@/components/ConfirmDialog';
import { formatApiError } from '@/utils/apiError';

/**
 * Category drill-down — read-only qty list for normal staff, with Add / Delete
 * product controls for admin & manager users.
 */
export default function StockCategory() {
  const navigate = useNavigate();
  const { isStaff, isAdmin, isManager } = useContext(AuthContext);
  const canEditCatalogue = isAdmin || isManager;
  const confirm = useConfirm();
  const [sp] = useSearchParams();
  const categoryName = sp.get('name') || '';

  const [products, setProducts] = useState([]);
  const [allCategories, setAllCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddSku, setShowAddSku] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [moveProduct, setMoveProduct] = useState(null);
  const [limitsProduct, setLimitsProduct] = useState(null);
  const [limitsForm, setLimitsForm] = useState({ min_stock: '', max_stock: '' });
  const [limitsBusy, setLimitsBusy] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [prodRes, catsRes] = await Promise.all([
        fetch(`${API_URL}/api/stock/products?category=${encodeURIComponent(categoryName)}`, { credentials: 'include' }),
        fetch(`${API_URL}/api/stock/categories`, { credentials: 'include' }),
      ]);
      if (!prodRes.ok) throw new Error('Failed to load products');
      if (!catsRes.ok) throw new Error('Failed to load categories');
      setProducts(await prodRes.json());
      setAllCategories(await catsRes.json());
    } catch (e) {
      toast.error(e.message);
    } finally {
      setLoading(false);
    }
  }, [categoryName]);

  useEffect(() => {
    if (!isStaff) { navigate('/'); return; }
    if (!categoryName) { navigate('/staff/stock'); return; }
    load();
  }, [isStaff, navigate, load, categoryName]);

  // Category-scoped product filter — name match only (single category, no
  // need to also match category name like the brand/global searches do).
  const visible = useMemo(() => {
    const q = searchTerm.trim().toLowerCase();
    if (!q) return products;
    return products.filter(p => p.name.toLowerCase().includes(q));
  }, [products, searchTerm]);

  const deleteProduct = async (p) => {
    const ok = await confirm({
      title: `Remove "${p.name}"?`,
      body: p.qty_on_hand > 0
        ? `⚠️ This product still has ${p.qty_on_hand} units on hand. Delete anyway?`
        : 'This cannot be undone.',
      confirmLabel: 'Delete',
      tone: 'danger',
    });
    if (!ok) return;
    try {
      const res = await fetch(`${API_URL}/api/stock/products/${p.id}`, { method: 'DELETE', credentials: 'include' });
      if (!res.ok) throw new Error(formatApiError(await res.json()) || 'Failed');
      toast.success(`${p.name} removed.`);
      await load();
    } catch (e) {
      toast.error(e.message);
    }
  };

  const renameProduct = async (p) => {
    // Native prompt keeps this very low-friction — one tap, type, done.
    // The backend cascades the new name across stock_movements so the audit
    // trail stays coherent.
    const next = window.prompt(`Rename "${p.name}" to:`, p.name);
    if (next === null) return;
    const trimmed = next.trim();
    if (!trimmed || trimmed === p.name) return;
    try {
      const res = await fetch(`${API_URL}/api/stock/products/${p.id}`, {
        method: 'PUT', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name: trimmed }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(formatApiError(data) || 'Failed');
      toast.success(`Renamed to "${data.name}".`);
      await load();
    } catch (e) {
      toast.error(e.message);
    }
  };

  const renameCategory = async () => {
    const next = window.prompt(`Rename category "${categoryName}" to:`, categoryName);
    if (next === null) return;
    const trimmed = next.trim();
    if (!trimmed || trimmed === categoryName) return;
    try {
      const res = await fetch(`${API_URL}/api/stock/categories/${encodeURIComponent(categoryName)}`, {
        method: 'PUT', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name: trimmed }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(formatApiError(data) || 'Failed');
      toast.success(`Category renamed to "${data.name}".`);
      // Reload the page with the new ?name= so subsequent fetches hit the
      // renamed category. `replace` so the back button skips the now-dead URL.
      navigate(`/staff/stock/category?name=${encodeURIComponent(data.name)}`, { replace: true });
    } catch (e) {
      toast.error(e.message);
    }
  };

  // Reorder by sending the new ordered_ids array to the backend. Optimistic
  // local update so the row visibly jumps before the round-trip completes.
  const reorderTo = async (productIds) => {
    setProducts(prev => productIds.map(id => prev.find(p => p.id === id)).filter(Boolean));
    try {
      const res = await fetch(`${API_URL}/api/stock/products/reorder`, {
        method: 'POST', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ category: categoryName, ordered_ids: productIds }),
      });
      if (!res.ok) throw new Error(formatApiError(await res.json()) || 'Failed to reorder');
    } catch (e) {
      toast.error(e.message);
      await load(); // rollback to server state
    }
  };

  const moveUp = (idx) => {
    if (idx <= 0) return;
    const next = products.map(p => p.id);
    [next[idx - 1], next[idx]] = [next[idx], next[idx - 1]];
    reorderTo(next);
  };

  const moveDown = (idx) => {
    if (idx >= products.length - 1) return;
    const next = products.map(p => p.id);
    [next[idx], next[idx + 1]] = [next[idx + 1], next[idx]];
    reorderTo(next);
  };

  const handleMove = async (target) => {
    try {
      const res = await fetch(`${API_URL}/api/stock/products/${moveProduct.id}/move`, {
        method: 'POST', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ category: target }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(formatApiError(data) || 'Failed');
      toast.success(`${moveProduct.name} → ${target}`);
      setMoveProduct(null);
      await load();
    } catch (e) {
      toast.error(e.message);
    }
  };

  const openLimits = (p) => {
    setLimitsForm({
      min_stock: p.min_stock == null ? '' : String(p.min_stock),
      max_stock: p.max_stock == null ? '' : String(p.max_stock),
    });
    setLimitsProduct(p);
  };

  const saveLimits = async () => {
    if (!limitsProduct) return;
    // Empty string → null (clears the limit). Otherwise parse to int.
    const payload = {
      min_stock: limitsForm.min_stock === '' ? null : Number(limitsForm.min_stock),
      max_stock: limitsForm.max_stock === '' ? null : Number(limitsForm.max_stock),
    };
    if (payload.min_stock != null && (isNaN(payload.min_stock) || payload.min_stock < 0)) {
      toast.error('Min stock must be 0 or higher.');
      return;
    }
    if (payload.max_stock != null && (isNaN(payload.max_stock) || payload.max_stock < 0)) {
      toast.error('Max stock must be 0 or higher.');
      return;
    }
    setLimitsBusy(true);
    try {
      const res = await fetch(`${API_URL}/api/stock/products/${limitsProduct.id}/limits`, {
        method: 'PUT', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(formatApiError(data) || 'Failed to save limits');
      toast.success(`Limits updated for ${limitsProduct.name}`);
      setLimitsProduct(null);
      await load();
    } catch (e) {
      toast.error(e.message);
    } finally {
      setLimitsBusy(false);
    }
  };

  const syncMetallicPigments = async () => {
    // Idempotent admin tool — safe to click repeatedly. Creates missing 1kg
    // siblings for every Metallic Pigment 100g - <Colour> and orders them
    // 100g/1kg as pairs.
    const ok = await confirm({
      title: 'Add missing 1kg variants?',
      body: 'This creates a 1kg row for any metallic pigment colour that\u2019s only listed as 100g. Re-running has no effect — existing 1kg rows are left untouched. New rows are created with on-hand = 0.',
      confirmLabel: 'Run sync',
      tone: 'primary',
    });
    if (!ok) return;
    try {
      const res = await fetch(`${API_URL}/api/stock/admin/sync-metallic-pigment-1kg-variants`, {
        method: 'POST', credentials: 'include',
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(formatApiError(data) || 'Failed');
      toast.success(
        data.created
          ? `Created ${data.created} new 1kg variants. ${data.paired} colours now paired.`
          : `All ${data.paired} colours already have both sizes — nothing to do.`
      );
      await load();
    } catch (e) {
      toast.error(e.message);
    }
  };

  if (!isStaff) return null;

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="text-white" style={{ background: 'linear-gradient(135deg, #0B0B0E 0%, #1f4030 75%)', borderBottom: '3px solid #D4A24B' }}>
        <div className="max-w-[1500px] mx-auto px-3 sm:px-4 py-4 sm:py-5 flex items-center gap-2 sm:gap-3">
          <button onClick={() => navigate(-1)} data-testid="stock-cat-back" className="p-2 hover:bg-white/10 rounded-lg shrink-0" aria-label="Back">
            <ArrowLeft className="w-5 h-5" />
          </button>
          <div className="flex-1 min-w-0">
            <h1 className="text-base sm:text-2xl font-bold flex items-center gap-2 truncate"><Warehouse className="w-5 h-5 sm:w-6 sm:h-6 shrink-0" style={{ color: '#D4A24B' }} /> <span className="truncate">{categoryName}</span></h1>
            <p className="hidden sm:block text-white/70 text-sm mt-0.5">{products.length} SKUs in this category</p>
          </div>
          {canEditCatalogue && categoryName === 'Metallic Pigments' && (
            <Button onClick={syncMetallicPigments} variant="outline" size="sm" className="bg-white/10 border-white/30 text-white hover:bg-white/20 shrink-0 px-2 sm:px-3" data-testid="sync-metallic-pigments-btn" aria-label="Add missing 1kg variants" title="Add missing 1kg variants">
              <Plus className="w-4 h-4 sm:mr-1.5" /><span className="hidden sm:inline">Add 1kg variants</span>
            </Button>
          )}
          {canEditCatalogue && (
            <Button onClick={renameCategory} variant="outline" size="sm" className="bg-white/10 border-white/30 text-white hover:bg-white/20 shrink-0 px-2 sm:px-3" data-testid="rename-category-btn" aria-label="Rename category" title="Rename category">
              <Pencil className="w-4 h-4 sm:mr-1.5" /><span className="hidden sm:inline">Rename</span>
            </Button>
          )}
          {canEditCatalogue && (
            <Button onClick={() => setShowAddSku(true)} variant="outline" size="sm" className="bg-white/10 border-white/30 text-white hover:bg-white/20 shrink-0 px-2 sm:px-3" data-testid="add-sku-btn" aria-label="Add product">
              <Plus className="w-4 h-4 sm:mr-1.5" /><span className="hidden sm:inline">Add product</span>
            </Button>
          )}
          <Button onClick={() => navigate('/staff')} variant="outline" size="sm" className="bg-white/10 border-white/30 text-white hover:bg-white/20 shrink-0 px-2 sm:px-3" aria-label="Staff">
            <Home className="w-4 h-4 sm:mr-1.5" /><span className="hidden sm:inline">Staff</span>
          </Button>
        </div>
      </div>

      <div className="max-w-[1500px] mx-auto px-4 py-5">
        {/* Category-scoped search — filters this category's product list only. */}
        <div className="mb-4 relative">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
          <Input
            data-testid="stock-cat-search"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            placeholder={`Search products in ${categoryName}…`}
            className="pl-9 pr-10 h-11"
          />
          {searchTerm && (
            <button
              onClick={() => setSearchTerm('')}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-700"
              data-testid="stock-cat-search-clear"
              aria-label="Clear search"
            >
              <XCircle className="w-5 h-5" />
            </button>
          )}
        </div>

        {loading ? (
          <div className="text-center py-16 text-gray-500">Loading…</div>
        ) : visible.length === 0 ? (
          <div className="text-center py-16 text-gray-500 italic">
            {searchTerm ? `— nothing matches "${searchTerm}" in this category —` : '— no products —'}
          </div>
        ) : (
          <Card className="divide-y divide-gray-100">
            {visible.map((p, idx) => {
              // Up/down only meaningful against the unfiltered list. When the
              // user is searching we hide them so they can't accidentally
              // reorder by a position that doesn't match what they see.
              const fullIdx = searchTerm ? -1 : idx;
              const showReorder = canEditCatalogue && !searchTerm;
              return (
                <div
                  key={p.id}
                  data-testid={`stock-row-${p.id}`}
                  className="px-2 py-3 sm:p-4 hover:bg-gray-50 sm:flex sm:items-center sm:gap-2"
                >
                  {/* Top row on mobile / left cluster on desktop: reorder + name + qty */}
                  <div className="flex items-center gap-1.5 sm:gap-2 flex-1 min-w-0">
                    {showReorder && (
                      <div className="flex gap-1 shrink-0">
                        <button
                          onClick={() => moveUp(fullIdx)}
                          disabled={fullIdx === 0}
                          data-testid={`stock-up-${p.id}`}
                          aria-label={`Move ${p.name} up`}
                          className="w-10 h-10 flex items-center justify-center rounded-lg border border-gray-200 bg-white hover:bg-gray-100 active:bg-gray-200 disabled:opacity-25 disabled:cursor-not-allowed text-gray-700 shadow-sm"
                        >
                          <ChevronUp className="w-5 h-5" />
                        </button>
                        <button
                          onClick={() => moveDown(fullIdx)}
                          disabled={fullIdx === products.length - 1}
                          data-testid={`stock-down-${p.id}`}
                          aria-label={`Move ${p.name} down`}
                          className="w-10 h-10 flex items-center justify-center rounded-lg border border-gray-200 bg-white hover:bg-gray-100 active:bg-gray-200 disabled:opacity-25 disabled:cursor-not-allowed text-gray-700 shadow-sm"
                        >
                          <ChevronDown className="w-5 h-5" />
                        </button>
                      </div>
                    )}
                    <div className="flex-1 min-w-0">
                      <div className="font-semibold text-gray-900 break-words leading-tight">{p.name}</div>
                      {(p.min_stock != null || p.max_stock != null) && (
                        <div className="text-[10px] text-gray-500 mt-0.5" data-testid={`stock-limits-${p.id}`}>
                          Min {p.min_stock ?? '—'} · Max {p.max_stock ?? '—'}
                        </div>
                      )}
                    </div>
                    <div className="text-right shrink-0 px-1 sm:px-2">
                      <div className="hidden sm:block text-[10px] uppercase text-gray-500">On hand</div>
                      <div className="text-xl sm:text-2xl font-bold leading-none" data-testid={`stock-qty-${p.id}`}>{p.qty_on_hand}</div>
                      {p.min_stock != null && p.qty_on_hand <= p.min_stock && (
                        <span
                          className="mt-1 inline-block text-[9px] font-bold uppercase tracking-wider bg-rose-100 text-rose-700 px-1.5 py-0.5 rounded"
                          data-testid={`stock-badge-low-${p.id}`}
                        >
                          Low
                        </span>
                      )}
                      {p.min_stock == null && p.max_stock != null && p.qty_on_hand >= p.max_stock && (
                        <span
                          className="mt-1 inline-block text-[9px] font-bold uppercase tracking-wider bg-amber-100 text-amber-800 px-1.5 py-0.5 rounded"
                          data-testid={`stock-badge-over-${p.id}`}
                        >
                          Over
                        </span>
                      )}
                    </div>
                  </div>
                  {/* Action buttons. On mobile they sit on their own line under
                      the name (aligned past the reorder arrows for visual
                      anchoring). On desktop they stay inline on the right. */}
                  {canEditCatalogue && (
                    <div
                      className={`flex items-center gap-2 mt-2 sm:mt-0 sm:shrink-0 ${showReorder ? 'pl-[88px] sm:pl-0' : 'pl-0'}`}
                      data-testid={`stock-actions-${p.id}`}
                    >
                      <Button
                        data-testid={`stock-rename-${p.id}`}
                        size="sm" variant="outline"
                        onClick={() => renameProduct(p)}
                        aria-label={`Rename ${p.name}`}
                        title="Rename"
                        className="border-gray-200 text-gray-600 hover:bg-gray-50 shrink-0 h-9 w-9 sm:h-9 sm:w-9 p-0"
                      >
                        <Pencil className="w-4 h-4" />
                      </Button>
                      <Button
                        data-testid={`stock-limits-btn-${p.id}`}
                        size="sm" variant="outline"
                        onClick={() => openLimits(p)}
                        aria-label={`Set min/max stock for ${p.name}`}
                        title="Set min/max stock levels"
                        className="border-gray-200 text-gray-600 hover:bg-gray-50 shrink-0 h-9 w-9 sm:h-9 sm:w-9 p-0"
                      >
                        <Gauge className="w-4 h-4" />
                      </Button>
                      <Button
                        data-testid={`stock-move-${p.id}`}
                        size="sm" variant="outline"
                        onClick={() => setMoveProduct(p)}
                        aria-label={`Move ${p.name} to another category`}
                        title="Move to another category"
                        className="border-gray-200 text-gray-600 hover:bg-gray-50 shrink-0 h-9 w-9 sm:h-9 sm:w-9 p-0"
                      >
                        <FolderInput className="w-4 h-4" />
                      </Button>
                      <Button
                        data-testid={`stock-delete-${p.id}`}
                        size="sm" variant="outline"
                        onClick={() => deleteProduct(p)}
                        aria-label={`Delete ${p.name}`}
                        title="Delete"
                        className="border-red-200 text-red-600 hover:bg-red-50 shrink-0 h-9 w-9 sm:h-9 sm:w-9 p-0"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  )}
                </div>
              );
            })}
          </Card>
        )}
      </div>

      {showAddSku && (
        <AddSkuModal category={categoryName} onClose={() => setShowAddSku(false)} onDone={async () => { setShowAddSku(false); await load(); }} />
      )}
      {moveProduct && (
        <MoveProductModal
          product={moveProduct}
          currentCategory={categoryName}
          allCategories={allCategories}
          onClose={() => setMoveProduct(null)}
          onMove={handleMove}
        />
      )}

      <Dialog open={!!limitsProduct} onOpenChange={(o) => !o && !limitsBusy && setLimitsProduct(null)}>
        <DialogContent data-testid="stock-limits-modal" className="max-w-sm">
          <DialogHeader>
            <DialogTitle>Set stock levels</DialogTitle>
            <DialogDescription>
              <strong>{limitsProduct?.name}</strong> — currently <strong>{limitsProduct?.qty_on_hand ?? 0}</strong> on hand.
              Leave a field blank to clear that limit.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label htmlFor="limit-min">Min stock (LOW alert triggers at or below)</Label>
              <Input
                id="limit-min"
                data-testid="stock-limit-min-input"
                type="number"
                inputMode="numeric"
                min="0"
                step="1"
                value={limitsForm.min_stock}
                onChange={(e) => setLimitsForm(f => ({ ...f, min_stock: e.target.value }))}
                placeholder="e.g. 5"
                className="mt-1"
                autoFocus
              />
            </div>
            <div>
              <Label htmlFor="limit-max">Max stock (OVER alert triggers at or above)</Label>
              <Input
                id="limit-max"
                data-testid="stock-limit-max-input"
                type="number"
                inputMode="numeric"
                min="0"
                step="1"
                value={limitsForm.max_stock}
                onChange={(e) => setLimitsForm(f => ({ ...f, max_stock: e.target.value }))}
                placeholder="e.g. 100"
                className="mt-1"
              />
            </div>
            <p className="text-[11px] text-gray-500">
              These thresholds power the LOW/OVER badges on this page and the daily 09:00 stock digest email.
            </p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setLimitsProduct(null)} disabled={limitsBusy}>Cancel</Button>
            <Button onClick={saveLimits} disabled={limitsBusy} data-testid="stock-limits-save" className="bg-slate-800 hover:bg-slate-900 text-white">
              {limitsBusy ? 'Saving…' : 'Save levels'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function AddSkuModal({ category, onClose, onDone }) {
  const [name, setName] = useState('');
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    const n = name.trim();
    if (!n) { toast.error('Enter a product name.'); return; }
    setBusy(true);
    try {
      const res = await fetch(`${API_URL}/api/stock/products`, {
        method: 'POST', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ category, name: n }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(formatApiError(data) || 'Failed');
      toast.success(`Added "${data.name}".`);
      onDone();
    } catch (e) {
      toast.error(e.message);
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl w-full max-w-md shadow-xl" onClick={(e) => e.stopPropagation()}>
        <div className="p-5 border-b border-gray-100">
          <h2 className="text-lg font-bold text-gray-900">Add product</h2>
          <p className="text-xs text-gray-500 mt-1">Category: <strong>{category}</strong></p>
        </div>
        <div className="p-5">
          <Label htmlFor="addsku-name" className="text-sm font-semibold">Product name</Label>
          <Input id="addsku-name" data-testid="add-sku-name" value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. New Coating 5kg" className="mt-1" autoFocus />
        </div>
        <div className="p-4 border-t border-gray-100 flex justify-end gap-2">
          <Button variant="outline" size="sm" onClick={onClose} data-testid="add-sku-cancel">Cancel</Button>
          <Button size="sm" onClick={submit} disabled={busy} data-testid="add-sku-submit" className="bg-slate-800 hover:bg-slate-900 text-white">{busy ? 'Saving…' : 'Add product'}</Button>
        </div>
      </div>
    </div>
  );
}

function MoveProductModal({ product, currentCategory, allCategories, onClose, onMove }) {
  // Strip the current category — moving to the same place is a no-op.
  const targets = allCategories.filter(c => c.category !== currentCategory);
  const [target, setTarget] = useState(targets[0]?.category || '');
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    if (!target) { toast.error('Pick a category.'); return; }
    setBusy(true);
    try {
      await onMove(target);
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl w-full max-w-md shadow-xl" onClick={(e) => e.stopPropagation()}>
        <div className="p-5 border-b border-gray-100">
          <h2 className="text-lg font-bold text-gray-900 flex items-center gap-2">
            <FolderInput className="w-5 h-5 text-gray-700" /> Move product
          </h2>
          <p className="text-xs text-gray-500 mt-1">
            <strong>{product.name}</strong> · currently in <strong>{currentCategory}</strong>
          </p>
        </div>
        <div className="p-5">
          <Label htmlFor="move-target" className="text-sm font-semibold">Move to category</Label>
          {targets.length === 0 ? (
            <p className="mt-2 text-sm text-gray-500 italic">No other categories yet — create one first from the brand page.</p>
          ) : (
            <select
              id="move-target"
              data-testid="move-product-target"
              value={target}
              onChange={(e) => setTarget(e.target.value)}
              className="mt-1 w-full border-2 rounded-lg p-2.5 bg-white text-sm"
            >
              {targets.map(c => (
                <option key={c.category} value={c.category}>
                  {c.category} <span className="text-gray-400">— {c.product_count} SKUs</span>
                </option>
              ))}
            </select>
          )}
          <p className="text-[11px] text-gray-500 mt-2">
            The product will be slotted at the end of the target category. On-hand quantity and movement history stay with the product.
          </p>
        </div>
        <div className="p-4 border-t border-gray-100 flex justify-end gap-2">
          <Button variant="outline" size="sm" onClick={onClose} data-testid="move-product-cancel">Cancel</Button>
          <Button
            size="sm"
            onClick={submit}
            disabled={busy || !target}
            data-testid="move-product-submit"
            className="bg-slate-800 hover:bg-slate-900 text-white"
          >
            {busy ? 'Moving…' : 'Move product'}
          </Button>
        </div>
      </div>
    </div>
  );
}

